// react
import React, { useState } from 'react';
import { useSelector } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import CloseIcon from '@mui/icons-material/Close';

// material ui
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
} from '@mui/material';

// api
import type { BenchmarkDataPoint } from '@/api/generated/energyAnalysis/endpoints/benchmarking';
import { useCalculateDetailedBenchmarkDataDetailedBenchmarkAccountIdMatrikelKeyGetQuery } from '@/api/generated/energyAnalysis/endpoints/benchmarking';

// shared components
import InfoTooltipIcon from '@/components/ui/icon/InfoTooltipIcon';

// feature-components
import type { BenchmarkChartData } from '@/features/energyxplorer/portfolio/components/datagrid/cells/benchmark-graph/benchmark';
import PortfolioAnalysisBenchmarkChart from '@/features/energyxplorer/portfolio/components/datagrid/cells/benchmark-graph/PortfolioAnalysisBenchmarkChart';
// components
import { getSelectedAccount } from '@/redux/selectedAccountSlice';

interface BenchmarkChipProps {
  benchmarkData: BenchmarkDataPoint[];
  matrikelKey: string;
  energyType: string;
}

const BenchmarkChip = ({
  benchmarkData,
  matrikelKey,
  energyType,
}: BenchmarkChipProps) => {
  const { t } = useTranslation('portfolio-analysis');
  const [open, setOpen] = useState(false);
  const account = useSelector(getSelectedAccount);

  // Only fetch chart data when dialog is open - skip prevents API call until then
  const {
    currentData: benchmarkChartData,
    isFetching: isBenchmarkChartDataFetching,
  } =
    useCalculateDetailedBenchmarkDataDetailedBenchmarkAccountIdMatrikelKeyGetQuery(
      {
        accountId: account?.AccountId,
        matrikelKey: matrikelKey,
      },
      { skip: !open || !matrikelKey }, // No API request until dialog opens
    );

  // Errors from benchmark data fetch are handled by apiErrorMiddleware.

  if (!benchmarkData) return null;

  // Find the benchmark data for this matrikel
  const data = benchmarkData.find((item) => item.MatrikelKey === matrikelKey);

  if (!data || (data.BenchmarkCDF == null && data.MedianDiff == null))
    return null;

  // Small sample size: grey chip — not enough data for reliable benchmark
  const groupCount = data.GroupCount ?? null;
  const isSampleTooSmall = groupCount !== null && groupCount < 20;

  // Determine chip color based on BenchmarkCDF (0 = best, 1 = worst)
  // CDF <= 0.25 -> dark green (better than 75% of peers)
  // CDF <= 0.50 -> yellow-green (better than 50%)
  // CDF <= 0.75 -> warning/orange
  // CDF >  0.75 -> error/red
  let chipMuiColor: 'warning' | 'error' | undefined;
  let chipCustomBgColor: string | undefined;
  let tooltipMessage;

  if (isSampleTooSmall) {
    tooltipMessage = t('benchmarkChip.sampleTooSmall');
    chipCustomBgColor = '#9e9e9e'; // grey
  } else {
    const cdf = data.BenchmarkCDF;
    if (cdf != null) {
      if (cdf <= 0.25) {
        chipCustomBgColor = '#2e7d32'; // dark green
      } else if (cdf <= 0.5) {
        chipCustomBgColor = '#c0d954'; // yellow-green
      } else if (cdf <= 0.75) {
        chipMuiColor = 'warning';
      } else {
        chipMuiColor = 'error';
      }
      tooltipMessage =
        cdf <= 0.5
          ? t('benchmarkChip.belowAverage')
          : t('benchmarkChip.aboveAverage');
    } else {
      // Fallback to MedianDiff if CDF unavailable
      const medianDiff = data.MedianDiff ?? 0;
      if (medianDiff <= -25) {
        chipCustomBgColor = '#2e7d32';
      } else if (medianDiff <= 0) {
        chipCustomBgColor = '#c0d954';
      } else if (medianDiff <= 25) {
        chipMuiColor = 'warning';
      } else {
        chipMuiColor = 'error';
      }
      tooltipMessage =
        medianDiff <= 0
          ? t('benchmarkChip.belowAverage')
          : t('benchmarkChip.aboveAverage');
    }
  }

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleCloseWithStop = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setOpen(false);
  };

  // Prepare data for the chart component using fetched chart data
  const chartData: Partial<BenchmarkChartData> =
    (benchmarkChartData as Partial<BenchmarkChartData>) || {
      BenchmarkData: {
        kWhPrYPrM2: data.kWhPrYPrM2,
        BenchmarkCDF: data.BenchmarkCDF,
        MedianDiff: data.MedianDiff,
        GroupMedian: data.GroupMedian,
      },
      BenchmarkPDF: [],
      BenchmarkHistogram: [],
    };

  return (
    <>
      <Tooltip title={tooltipMessage} placement="left">
        <Chip
          label=""
          color={chipMuiColor}
          size="small"
          onClick={handleClick}
          sx={{
            ml: 1,
            cursor: 'pointer',
            width: 20,
            height: 20,
            borderRadius: '50%',
            '& .MuiChip-label': {
              display: 'none',
            },
            ...(chipCustomBgColor && {
              backgroundColor: chipCustomBgColor,
              '&:hover': {
                backgroundColor: chipCustomBgColor,
                filter: 'brightness(0.9)',
              },
            }),
          }}
        />
      </Tooltip>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        onClick={(e) => e.stopPropagation()}
      >
        <DialogTitle>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box display="flex" alignItems="center">
              <Chip
                label={t('Beta')}
                variant="outlined"
                color="success"
                size="medium"
                sx={{ mr: 2 }}
              />
              {t('benchmarkChip.title', { energyType })}
              <InfoTooltipIcon title={t('benchmarkChip.description')} />
            </Box>
            <IconButton onClick={handleCloseWithStop} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <PortfolioAnalysisBenchmarkChart
            address={matrikelKey}
            data={chartData}
            isLoading={isBenchmarkChartDataFetching}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BenchmarkChip;
